import { Link } from 'react-router-dom';
import { BookOpen, CalendarClock, ClipboardList, ExternalLink, Target, Video } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  ProgressBar,
  StatCard,
} from '@tutor/ui';
import { useStudentDashboard } from '../../hooks/queries';
import { LessonCard } from '../../components/LessonCard';
import { HomeworkCard } from '../../components/HomeworkCard';
import { PendingInvitations } from '../../components/PendingInvitations';
import { formatAgo, formatRelativeDayTime, formatScore } from '../../lib/format';

export function StudentDashboardPage() {
  const { data, isLoading, isError, refetch } = useStudentDashboard();

  if (isLoading) return <LoadingState label="Loading your dashboard…" />;
  if (isError || !data) {
    return (
      <ErrorState
        description="We could not load your dashboard."
        action={
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Try again
          </button>
        }
      />
    );
  }

  const { nextLesson } = data;

  return (
    <>
      <PageHeader
        title={`Hello ${data.greeting.name}`}
        description="Here is your day at a glance."
      />

      <PendingInvitations />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Lessons this week"
          value={data.stats.lessonsThisWeek}
          icon={<BookOpen className="h-4 w-4" aria-hidden />}
          tone="brand"
        />
        <StatCard
          label="Homework due"
          value={data.stats.homeworkDue}
          hint={data.stats.homeworkOverdue > 0 ? `${data.stats.homeworkOverdue} overdue` : 'Nothing overdue'}
          icon={<ClipboardList className="h-4 w-4" aria-hidden />}
          tone={data.stats.homeworkOverdue > 0 ? 'danger' : 'neutral'}
        />
        <StatCard
          label="Average progress"
          value={data.stats.averageProgress !== null ? `${data.stats.averageProgress}%` : '—'}
          icon={<Target className="h-4 w-4" aria-hidden />}
          tone="success"
        />
        <StatCard
          label="Resources"
          value={data.resources.length}
          hint="Shared by your tutors"
          icon={<ExternalLink className="h-4 w-4" aria-hidden />}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* The next lesson is the single most important thing on this page, so it
              gets a full-width hero rather than a row in a list. */}
          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 bg-brand-600 px-5 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-100">Next lesson</p>
            </div>
            <CardBody>
              {!nextLesson ? (
                <EmptyState
                  title="No lessons scheduled"
                  description="When your tutor books your next lesson it will show up here."
                  icon={<CalendarClock className="h-5 w-5" aria-hidden />}
                />
              ) : (
                <div className="flex flex-wrap items-start gap-5">
                  <Avatar name={nextLesson.tutor.fullName} src={nextLesson.tutor.avatarUrl} size="xl" />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold text-slate-900">
                        {nextLesson.subject?.name ?? nextLesson.title}
                      </h2>
                      {nextLesson.subject && <Badge tone="brand">{nextLesson.subject.name}</Badge>}
                    </div>

                    <p className="mt-1 text-sm text-slate-600">with {nextLesson.tutor.fullName}</p>

                    <p className="mt-3 text-sm font-medium text-slate-900">
                      {formatRelativeDayTime(nextLesson.scheduledStart)}
                    </p>

                    {nextLesson.topic && (
                      <div className="mt-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Topic</p>
                        <p className="mt-0.5 text-sm text-slate-900">{nextLesson.topic.name}</p>
                      </div>
                    )}

                    <div className="mt-5 flex flex-wrap gap-2">
                      {nextLesson.canJoin ? (
                        <Link to={`/student/classroom/${nextLesson.id}`}>
                          <Button leftIcon={<Video className="h-4 w-4" aria-hidden />}>Join Classroom</Button>
                        </Link>
                      ) : (
                        <Button disabled leftIcon={<Video className="h-4 w-4" aria-hidden />}>
                          Opens 15 minutes before
                        </Button>
                      )}
                      <Link to={`/student/lessons/${nextLesson.id}`}>
                        <Button variant="outline">Lesson details</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Homework Due"
              action={
                <Link to="/student/homework" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  View all
                </Link>
              }
            />
            <CardBody className="space-y-3">
              {data.homeworkDue.length === 0 ? (
                <EmptyState
                  title="Nothing due"
                  description="You are all caught up. Nice work."
                  icon={<ClipboardList className="h-5 w-5" aria-hidden />}
                />
              ) : (
                data.homeworkDue.map((assignment) => (
                  <HomeworkCard key={assignment.id} assignment={assignment} perspective="student" />
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Upcoming Lessons" />
            <CardBody className="space-y-3">
              {data.upcomingLessons.length === 0 ? (
                <EmptyState title="Nothing else scheduled" icon={<CalendarClock className="h-5 w-5" aria-hidden />} />
              ) : (
                data.upcomingLessons.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} perspective="student" showDay />
                ))
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Your Progress"
              action={
                <Link to="/student/progress" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  Details
                </Link>
              }
            />
            <CardBody>
              {data.progress.length === 0 ? (
                <EmptyState title="No progress recorded yet" icon={<Target className="h-5 w-5" aria-hidden />} />
              ) : (
                <div className="space-y-4">
                  {data.progress.map((subject) => (
                    <ProgressBar
                      key={subject.id}
                      label={subject.subject.name}
                      value={subject.overallPercentage}
                    />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Tutor Feedback" />
            <CardBody>
              {data.tutorFeedback.length === 0 ? (
                <EmptyState title="No feedback yet" description="Feedback appears once your work is marked." />
              ) : (
                <ul className="space-y-4">
                  {data.tutorFeedback.map((feedback) => (
                    <li key={feedback.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={feedback.tutorName} src={feedback.tutorAvatarUrl} size="xs" />
                        <p className="text-sm font-medium text-slate-900">{feedback.tutorName}</p>
                        {feedback.score !== null && (
                          <Badge tone="success" className="ml-auto">
                            {formatScore(feedback.score, feedback.maxScore)}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1.5 text-xs font-medium text-slate-500">{feedback.homeworkTitle}</p>
                      <p className="mt-1.5 text-sm text-slate-700">{feedback.comment}</p>
                      <p className="mt-1.5 text-xs text-slate-400">{formatAgo(feedback.markedAt)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Recently Marked"
              action={
                <Link to="/student/homework" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  All homework
                </Link>
              }
            />
            <CardBody>
              {data.recentlyMarkedHomework.length === 0 ? (
                <EmptyState title="Nothing marked yet" />
              ) : (
                <ul className="space-y-2">
                  {data.recentlyMarkedHomework.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/student/homework/${item.id}`}
                        className="flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900">{item.homeworkTitle}</p>
                          {item.markedAt && (
                            <p className="text-xs text-slate-400">Marked {formatAgo(item.markedAt)}</p>
                          )}
                        </div>
                        <Badge tone="success">{formatScore(item.score, item.maxScore)}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Learning Resources"
              action={
                <Link to="/student/resources" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  View all
                </Link>
              }
            />
            <CardBody>
              {data.resources.length === 0 ? (
                <EmptyState title="No resources shared yet" />
              ) : (
                <ul className="space-y-2">
                  {data.resources.map((resource) => (
                    <li key={resource.id}>
                      <a
                        href={resource.externalUrl ?? resource.file?.url ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-lg p-2 text-sm hover:bg-slate-50"
                      >
                        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                        <span className="min-w-0 flex-1 truncate text-slate-900">{resource.title}</span>
                        {resource.subjectName && <Badge tone="neutral">{resource.subjectName}</Badge>}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
